import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash, Image as ImageIcon, X, CaretLeft, CaretRight, CloudArrowUp } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { compressImage } from "@/lib/imageCompress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MAX_PHOTOS = 40;

function fmtBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

/**
 * Photos tab: multi-file drag&drop, client-side compression to fit under the
 * 5MB backend limit, per-file upload progress, and lightbox viewer.
 */
export default function ProjectPhotosTab({ projectId, canWrite }) {
  const { t } = useLang();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState([]); // [{name, size, progress, status}]
  const [dragActive, setDragActive] = useState(false);
  const [lightbox, setLightbox] = useState(-1);   // index in photos, -1 = closed
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/photos`)
      .then((r) => setPhotos(r.data))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(-1);
      if (e.key === "ArrowRight") setLightbox((i) => Math.min(photos.length - 1, i + 1));
      if (e.key === "ArrowLeft") setLightbox((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, photos.length]);

  const uploadOne = async (file, idx) => {
    // Compress client-side if needed
    let payload = file;
    try {
      const compressed = await compressImage(file);
      payload = compressed.file;
      setUploading((prev) => prev.map((u, i) => i === idx ? { ...u, compressedSize: compressed.finalSize, progress: 20 } : u));
    } catch (_e) { /* fall back to original */ }

    const fd = new FormData();
    fd.append("file", payload);
    try {
      const { data } = await api.post(`/projects/${projectId}/photos`, fd, {
        onUploadProgress: (evt) => {
          const total = evt.total || payload.size;
          const pct = Math.min(95, 20 + Math.round(((evt.loaded || 0) / total) * 75));
          setUploading((prev) => prev.map((u, i) => i === idx ? { ...u, progress: pct } : u));
        },
      });
      setUploading((prev) => prev.map((u, i) => i === idx ? { ...u, progress: 100, status: "ok" } : u));
      setPhotos((p) => [data, ...p]);
      return true;
    } catch (err) {
      setUploading((prev) => prev.map((u, i) => i === idx ? { ...u, status: "err", error: apiError(err) } : u));
      toast.error(`${file.name}: ${apiError(err)}`);
      return false;
    }
  };

  const uploadFiles = async (files) => {
    const raw = Array.from(files || []);
    if (raw.length === 0) return;
    // Trust the file input's accept attribute + backend validation. Some browsers
    // (Android + HEIC, iOS after AirDrop, etc.) don't set file.type — filtering on
    // f.type.startsWith("image/") used to drop these silently. Now we allow anything
    // that has an image-like extension OR a MIME starting with image/. Everything else
    // gets a visible warning.
    const IMG_EXT = /\.(png|jpe?g|webp|heic|heif)$/i;
    const list = [];
    const rejected = [];
    raw.forEach((f) => {
      const okMime = (f.type || "").toLowerCase().startsWith("image/");
      const okExt = IMG_EXT.test(f.name || "");
      if (okMime || okExt) list.push(f);
      else rejected.push(f.name || "archivo");
    });
    if (rejected.length) {
      toast.warning(`No es imagen: ${rejected.join(", ")}`);
    }
    if (list.length === 0) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (list.length > remaining) {
      toast.warning(t("maxPhotosReached", { max: MAX_PHOTOS, remaining }));
      list.length = Math.max(0, remaining);
    }
    if (list.length === 0) return;
    const initial = list.map((f) => ({ name: f.name, size: f.size, progress: 0, status: "pending" }));
    setUploading(initial);
    let okCount = 0;
    // Sequential to avoid overwhelming backend / rate limits
    for (let i = 0; i < list.length; i++) {
      const ok = await uploadOne(list[i], i);
      if (ok) okCount += 1;
    }
    if (okCount > 0) toast.success(`${okCount} ${t("photoUploaded")}`);
    // Auto-clear after a brief pause
    setTimeout(() => setUploading([]), 2500);
  };

  const onInputChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      toast.info("No se seleccionó ninguna imagen");
      return;
    }
    // Copy the FileList to an array BEFORE clearing the input.value (some browsers
    // invalidate the reference after value reset).
    const copy = Array.from(files);
    e.target.value = "";
    uploadFiles(copy);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!canWrite) return;
    uploadFiles(e.dataTransfer.files);
  };

  const remove = async (photoId) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/projects/${projectId}/photos/${photoId}`);
      setPhotos((p) => p.filter((x) => x.id !== photoId));
    } catch (e) { toast.error(apiError(e)); }
  };

  const updateCaption = async (photoId, caption) => {
    try {
      await api.put(`/projects/${projectId}/photos/${photoId}`, { caption });
      setPhotos((p) => p.map((x) => (x.id === photoId ? { ...x, caption } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {canWrite && (
          <>
            <Button
              data-testid="upload-photo-button"
              onClick={() => fileRef.current?.click()}
              disabled={photos.length >= MAX_PHOTOS}
              className="bg-blue-900 hover:bg-blue-800 gap-2"
            >
              <ImageIcon size={16} weight="bold" /> {t("addPhoto")}
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              className="hidden"
              data-testid="photo-file-input"
              onChange={onInputChange}
            />
          </>
        )}
        <p className="text-xs text-slate-500 ml-2" data-testid="photos-counter">
          {photos.length}/{MAX_PHOTOS} · {t("photosHint")}
        </p>
      </div>

      {canWrite && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          data-testid="photo-dropzone"
          className={
            "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors " +
            (dragActive ? "bg-blue-50 border-blue-400" : "bg-slate-50/60 border-slate-300 hover:border-blue-400 hover:bg-blue-50/40")
          }
        >
          <CloudArrowUp size={28} weight="duotone" className="mx-auto text-blue-900 mb-1" />
          <p className="text-sm text-slate-600">{t("photoDropzoneHint")}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{t("photoAutoCompress")}</p>
        </div>
      )}

      {uploading.length > 0 && (
        <div className="space-y-1.5" data-testid="upload-progress-list">
          {uploading.map((u, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-md p-2 flex items-center gap-3 text-xs">
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-slate-800">{u.name}</p>
                <p className="text-[10px] text-slate-400">
                  {fmtBytes(u.size)}
                  {u.compressedSize && u.compressedSize < u.size && <> → <span className="text-emerald-700">{fmtBytes(u.compressedSize)}</span></>}
                  {u.error && <span className="text-red-600"> · {u.error}</span>}
                </p>
                <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                  <div
                    className={"h-full transition-all " + (u.status === "err" ? "bg-red-500" : "bg-blue-600")}
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              </div>
              <span className={"text-[10px] font-semibold shrink-0 " + (u.status === "err" ? "text-red-600" : u.status === "ok" ? "text-emerald-600" : "text-slate-500")}>
                {u.status === "err" ? "!" : u.status === "ok" ? "✓" : `${u.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}

      {photos.length ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-grid">
          {photos.map((ph, idx) => (
            <div key={ph.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden group" data-testid={`photo-card-${ph.id}`}>
              <button
                type="button"
                onClick={() => setLightbox(idx)}
                data-testid={`photo-open-${ph.id}`}
                className="aspect-video bg-slate-100 relative block w-full"
                aria-label={t("openPhoto")}
              >
                <img src={ph.data} alt={ph.caption || ph.filename} className="w-full h-full object-cover" />
                {canWrite && (
                  <span
                    role="button"
                    data-testid={`photo-delete-${ph.id}`}
                    onClick={(e) => { e.stopPropagation(); remove(ph.id); }}
                    className="absolute top-2 right-2 h-8 w-8 bg-white/95 border border-slate-200 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600"
                    aria-label={t("delete")}
                  >
                    <Trash size={14} weight="bold" />
                  </span>
                )}
              </button>
              <div className="p-3">
                {canWrite ? (
                  <Input
                    data-testid={`photo-caption-${ph.id}`}
                    defaultValue={ph.caption}
                    placeholder={t("caption")}
                    onBlur={(e) => { if (e.target.value !== (ph.caption || "")) updateCaption(ph.id, e.target.value); }}
                    className="text-xs h-8"
                  />
                ) : (
                  <p className="text-xs text-slate-700 truncate" title={ph.caption}>{ph.caption || ph.filename}</p>
                )}
                <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider">
                  {(ph.uploaded_at || "").slice(0, 10)} · {ph.uploaded_by} · {fmtBytes(ph.size)}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 bg-white border border-slate-200 rounded-lg p-10 text-center" data-testid="no-photos-empty">
          {t("noPhotos")}
        </p>
      )}

      {/* Lightbox */}
      {lightbox >= 0 && photos[lightbox] && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex flex-col"
          onClick={() => setLightbox(-1)}
          data-testid="photo-lightbox"
        >
          <div className="flex items-center justify-between p-3 text-white">
            <div>
              <p className="text-sm font-semibold">{photos[lightbox].caption || photos[lightbox].filename}</p>
              <p className="text-[10px] opacity-70">{(photos[lightbox].uploaded_at || "").slice(0, 10)} · {photos[lightbox].uploaded_by} · {lightbox + 1}/{photos.length}</p>
            </div>
            <button
              onClick={() => setLightbox(-1)}
              data-testid="lightbox-close"
              className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              aria-label={t("close") || "Close"}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="flex-1 relative flex items-center justify-center px-2 pb-6" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setLightbox((i) => Math.max(0, i - 1))}
              disabled={lightbox === 0}
              data-testid="lightbox-prev"
              className="absolute left-3 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center disabled:opacity-30"
              aria-label="prev"
            >
              <CaretLeft size={22} weight="bold" />
            </button>
            <img
              src={photos[lightbox].data}
              alt={photos[lightbox].caption || photos[lightbox].filename}
              className="max-h-[85vh] max-w-full object-contain"
            />
            <button
              onClick={() => setLightbox((i) => Math.min(photos.length - 1, i + 1))}
              disabled={lightbox === photos.length - 1}
              data-testid="lightbox-next"
              className="absolute right-3 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center disabled:opacity-30"
              aria-label="next"
            >
              <CaretRight size={22} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
